import { useEffect, useRef } from 'react'
import { useToast } from '@mytours/components'

import './styles/messageToast.less'

interface Props {
	isOpen: boolean
	message?: string
	/** Milliseconds from the end of the entrance until it dismisses itself; 0 keeps it up. */
	duration?: number
	position?: 'top' | 'bottom'
	onDidDismiss?: () => void
}

/** Shows a one-line toast while `isOpen`, with the props and timing of the Ionic toast it replaced. */
const MessageToast = ({ isOpen, message, duration = 0, position = 'bottom', onDidDismiss }: Props): null => {
	const { add, close, update } = useToast()
	const latest = useRef({ message, duration, position, onDidDismiss })
	latest.current = { message, duration, position, onDidDismiss }
	const openId = useRef<string>()

	useEffect(() => {
		if (!isOpen) return undefined
		const { message, duration, position } = latest.current
		let live = true
		const id = add({
			description: message,
			timeout: duration,
			data: { side: position },
			onRemove: () => {
				live = false
				openId.current = undefined
				latest.current.onDidDismiss?.()
			},
		})
		openId.current = id
		return () => {
			if (live) close(id)
		}
	}, [isOpen, add, close])

	useEffect(() => {
		if (openId.current) update(openId.current, { description: message })
	}, [message, update])

	return null
}

export default MessageToast
